import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentWorkflowControllerCancelAuthorisedSignatoryMutation } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { buildInvestmentProgrammeDocumentQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/documentQueryKeys';
import { getDocumentWorkflowEventsQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useDocumentWorkflowEvents';

interface Options {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	onSuccess?: () => void;
}

/** Withdraws an invited authorised signatory, returning the slot to its empty state. */
export const useCancelAuthorisedSignatory = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	onSuccess,
}: Options) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		...documentWorkflowControllerCancelAuthorisedSignatoryMutation(),
		onSuccess: async () => {
			showToastV2({
				state: 'success',
				title: tMipManage('documents_page.cancel_authorised_signatory.toast.success'),
			});
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: buildInvestmentProgrammeDocumentQueryKey({
						dealId,
						programmeId,
						documentWorkflowInstanceId,
					}),
				}),
				queryClient.invalidateQueries({
					queryKey: getDocumentWorkflowEventsQueryKey({
						programmeId,
						documentWorkflowInstanceId,
					}),
				}),
			]);
			onSuccess?.();
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('documents_page.cancel_authorised_signatory.toast.error'),
			});
		},
	});

	return {
		cancel: (signatureId: string) =>
			mutation.mutate({
				path: { dealId, programmeId, documentWorkflowInstanceId },
				body: { signatureId },
			}),
		isPending: mutation.isPending,
	};
};
